import { Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { LabelComponent } from '../uiComponents/LabelComponent';
import { AiReviewFieldNotice, useAiListingReview } from './AiListingReview';
import { EditorSection } from './EditorSection';
import { EditorNumericField, EditorSelectField } from './fields';
import { PropertyAdViewField } from './PropertyAdViewField';
import styles from './PropertyAdEditor.module.scss';
import {
  PROPERTY_AD_BATHS_OPTIONS,
  PROPERTY_AD_BEDS_OPTIONS,
  PROPERTY_AD_FLOOR_OPTIONS,
  PROPERTY_AD_FURNITURE_OPTIONS,
  PROPERTY_AD_OCCUPANCY_OPTIONS,
  PROPERTY_AD_RENOVATION_OPTIONS,
} from './propertyAdSpecificationOptions';
import {
  formatRentedTillForInput,
  getVisiblePropertySpecificationFields,
  parseRentedTillInput,
} from './propertyAdSpecifications';
import { usePropertyAdEditorNormalization } from './usePropertyAdEditorNormalization';

export const PropertySpecificationsSection = () => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const aiReview = useAiListingReview();
  const activityKind = useWatch({ control, name: 'activityKind' });
  const completionStatus = useWatch({ control, name: 'completionStatus' });
  const occupancy = useWatch({ control, name: 'occupancy' });
  const propertyKind = useWatch({ control, name: 'propertyKind' });
  const transactionType = useWatch({ control, name: 'transactionType' });
  const { changeOccupancy } = usePropertyAdEditorNormalization();

  const hasSectionReview = aiReview?.attention.some(
    (item) => item.target === 'section:specifications'
  );
  if (propertyKind === null && !hasSectionReview) return null;

  const visibility = getVisiblePropertySpecificationFields({
    activityKind,
    completionStatus,
    occupancy,
    propertyKind,
    transactionType,
  });

  return (
    <EditorSection
      cardTestId='property-specifications-card'
      description='Key characteristics and condition of the property'
      title='Property Specifications'
      titleId='property-specifications-title'
    >
      <AiReviewFieldNotice target='section:specifications' />
      <Box
        data-testid='property-specifications-grid'
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          minWidth: 0,
          width: '100%',
        }}
      >
        {visibility.beds && (
          <EditorSelectField
            label='Bedrooms'
            name='beds'
            options={PROPERTY_AD_BEDS_OPTIONS}
            placeholder='Select bedrooms'
            required
            rules={{ required: 'Beds is a required field' }}
          />
        )}

        {visibility.baths && (
          <EditorSelectField
            label='Baths'
            name='baths'
            options={PROPERTY_AD_BATHS_OPTIONS}
            placeholder='Select baths'
          />
        )}

        {visibility.builtUpArea && (
          <EditorNumericField
            label='Size (sqft)'
            name='builtUpArea'
            placeholder='Enter size (sqft)'
            required
            rules={{
              min: {
                message: 'Size of the selected property type cannot be less than 50 sqft',
                value: 50,
              },
              required: 'Size is a required field',
            }}
          />
        )}

        {visibility.view && <PropertyAdViewField />}

        {visibility.floor && (
          <EditorSelectField
            label='Floor'
            name='floor'
            options={PROPERTY_AD_FLOOR_OPTIONS}
            placeholder='Select floor'
          />
        )}

        {visibility.occupancy && (
          <EditorSelectField
            label='Availability'
            name='occupancy'
            options={PROPERTY_AD_OCCUPANCY_OPTIONS}
            placeholder='Select availability'
            onValueChange={changeOccupancy}
          />
        )}

        {visibility.rentedTill && (
          <Box className={styles.specificationInput} minWidth={0}>
            <LabelComponent label='Rented till' />
            <Controller
              control={control}
              name='rentedTill'
              render={({ field }) => (
                <TextField
                  fullWidth
                  inputProps={{
                    'aria-label': 'Rented till',
                    min: dayjs().format('YYYY-MM-DD'),
                  }}
                  type='date'
                  value={formatRentedTillForInput(field.value)}
                  onBlur={field.onBlur}
                  onChange={(event) => {
                    field.onChange(parseRentedTillInput(event.target.value));
                    aiReview?.resolveField('rentedTill');
                  }}
                />
              )}
            />
            <AiReviewFieldNotice target='rentedTill' />
          </Box>
        )}

        {visibility.furniture && (
          <EditorSelectField
            label='Furniture'
            name='furniture'
            options={PROPERTY_AD_FURNITURE_OPTIONS}
            placeholder='Select furniture'
          />
        )}

        {visibility.renovation && (
          <EditorSelectField
            label='Status'
            name='renovation'
            options={PROPERTY_AD_RENOVATION_OPTIONS}
            placeholder='Select status'
          />
        )}
      </Box>
    </EditorSection>
  );
};
